import React, { type PropsWithChildren } from 'react';
import { StyleSheet, View, type ViewStyle } from 'react-native';
import { Colors } from '../constants/Colors';
import { useDynamicStyles } from '../hooks';
import type { WithResponsive } from '../utils/responsivity';
import { isAndroid, isIos, isNative } from '../utils/responsivity';
import { TextBold, TextReg } from './StyledText';

type Props = {
  firstName?: string;
  lastName?: string;
  pronouns?: string;
  headline?: string;
  size?: 'small' | 'medium' | 'large';
  isLoading?: boolean;
  style?: ViewStyle;
  loadingStyle?: ViewStyle;
  nameButton?: React.ReactNode;
  isMobile?: boolean;
  isOneLineText?: boolean;
  align?: 'left' | 'center';
};

export function ProfileBasicInfo({
  firstName,
  lastName,
  pronouns,
  headline,
  size = 'medium',
  isLoading,
  style,
  loadingStyle,
  nameButton,
  isOneLineText,
  align = 'left',
}: PropsWithChildren<Props>) {
  const styles = useDynamicStyles(createStyles, {
    isMobile: false,
    isLoading,
    nameButton,
    align,
  });

  if (isLoading)
    return (
      <View
        style={[
          styles.textContainer,
          styles[`${size}InfoFiller`],
          loadingStyle || style,
        ]}
      >
        <View style={styles[`${size}NameFiller`]} />
        <View style={styles[`${size}PronounsFiller`]} />
        <View style={styles[`${size}HeadlineFiller`]} />
      </View>
    );

  return (
    <View style={[styles.textContainer, style]}>
      <View style={styles.nameContainer}>
        <TextBold
          style={styles[`${size}Name`]}
          numberOfLines={isOneLineText ? 1 : undefined}
        >
          {`${firstName || ''} ${lastName || ''}`.trim()}
        </TextBold>
        {nameButton}
      </View>
      {pronouns && (
        <TextReg
          style={styles[`${size}Pronouns`]}
          numberOfLines={isOneLineText ? 1 : undefined}
        >
          {`(${pronouns})`}
        </TextReg>
      )}
      {headline && (
        <TextReg
          style={styles[`${size}Headline`]}
          numberOfLines={isOneLineText ? 1 : undefined}
        >
          {headline}
        </TextReg>
      )}
    </View>
  );
}

const createStyles = ({
  isMobile,
  isLoading,
  nameButton,
  align = 'left',
}: WithResponsive<Props>) => {
  const desktopButtonHeightAdjustment = isMobile && nameButton ? 5 : 0;
  const iosButtonHeightAdjustment = nameButton ? 7.3 : 0;
  const androidButtonHeightAdjustment = nameButton ? 9.4 : 0;
  const largeIosButtonHeightAdjustment = nameButton ? 2 : 0;
  const largeAndroidButtonHeightAdjustment = nameButton ? 3 : 0;
  const isCentered = align === 'center';

  return StyleSheet.create({
    textContainer: {
      flexShrink: 1,
      justifyContent: 'center',
      alignItems: isCentered ? 'center' : 'flex-start',
      gap: isNative && !isLoading ? 3 : 0,
    },
    nameContainer: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: isCentered ? 'center' : 'flex-start',
      gap: 16,
      flexShrink: 1,
      overflow: 'hidden',
      width: '100%',
    },
    smallName: {
      fontSize: 16,
      textAlign: isCentered ? 'center' : 'left',
    },
    smallPronouns: {
      fontSize: 8,
      textAlign: isCentered ? 'center' : 'left',
    },
    smallHeadline: {
      fontSize: 12,
      textAlign: isCentered ? 'center' : 'left',
    },
    mediumName: {
      fontSize: isMobile ? 16 : 32,
      textAlign: isCentered ? 'center' : 'left',
    },
    mediumPronouns: {
      fontSize: isMobile ? 8 : 12,
      textAlign: isCentered ? 'center' : 'left',
    },
    mediumHeadline: {
      fontSize: isMobile ? 12 : 16,
      textAlign: isCentered ? 'center' : 'left',
    },
    largeName: {
      fontSize: isMobile ? 20 : 36,
      textAlign: isCentered ? 'center' : 'left',
    },
    largePronouns: {
      fontSize: isMobile ? 10 : 14,
      textAlign: isCentered ? 'center' : 'left',
    },
    largeHeadline: {
      fontSize: isMobile ? 14 : 20,
      textAlign: isCentered ? 'center' : 'left',
    },
    smallInfoFiller: {
      width: '100%',
      maxWidth: 200,
      height: isIos
        ? 57 + iosButtonHeightAdjustment
        : isAndroid
          ? 53.7 + androidButtonHeightAdjustment
          : 54 + desktopButtonHeightAdjustment,
      justifyContent: 'space-between',
      paddingVertical: 3,
    },
    mediumInfoFiller: {
      width: '100%',
      maxWidth: isMobile ? 200 : 400,
      height: isIos
        ? 57 + iosButtonHeightAdjustment
        : isAndroid
          ? 53.7 + androidButtonHeightAdjustment
          : isMobile
            ? 54 + desktopButtonHeightAdjustment
            : 91,
      justifyContent: 'space-between',
      paddingVertical: isMobile ? 2.5 : 6,
    },
    largeInfoFiller: {
      width: '100%',
      maxWidth: isMobile ? 200 : 400,
      height: isIos
        ? 67.7 + largeIosButtonHeightAdjustment
        : isAndroid
          ? 64.4 + largeAndroidButtonHeightAdjustment
          : isMobile
            ? 67
            : 106,
      justifyContent: 'space-between',
      paddingVertical: isNative ? 0 : 3,
    },
    smallNameFiller: {
      width: '100%',
      maxWidth: 140,
      height: 16,
      backgroundColor: Colors.opacity20,
      borderRadius: 8,
    },
    smallPronounsFiller: {
      width: '100%',
      maxWidth: 50,
      height: 8,
      backgroundColor: Colors.opacity20,
      borderRadius: 4,
    },
    smallHeadlineFiller: {
      width: '100%',
      maxWidth: 200,
      height: 12,
      backgroundColor: Colors.opacity20,
      borderRadius: 6,
    },
    mediumNameFiller: {
      width: '100%',
      maxWidth: isMobile ? 140 : 200,
      height: isMobile ? 16 : 32,
      backgroundColor: Colors.opacity20,
      borderRadius: 16,
    },
    mediumPronounsFiller: {
      width: '100%',
      maxWidth: isMobile ? 50 : 60,
      height: isMobile ? 8 : 12,
      backgroundColor: Colors.opacity20,
      borderRadius: 6,
    },
    mediumHeadlineFiller: {
      width: '100%',
      maxWidth: isMobile ? 200 : 400,
      height: isMobile ? 12 : 16,
      backgroundColor: Colors.opacity20,
      borderRadius: 8,
    },
    largeNameFiller: {
      width: '100%',
      maxWidth: isMobile ? 140 : 200,
      height: isMobile ? 20 : 36,
      marginTop: isMobile ? 5 : 8.5,
      backgroundColor: Colors.opacity20,
      borderRadius: 18,
    },
    largePronounsFiller: {
      width: '100%',
      maxWidth: isMobile ? 50 : 60,
      height: isMobile ? 8 : 14,
      backgroundColor: Colors.opacity20,
      borderRadius: 7,
    },
    largeHeadlineFiller: {
      width: '100%',
      maxWidth: isMobile ? 200 : 400,
      height: isMobile ? 12 : 20,
      marginBottom: isMobile ? 3 : 5,
      backgroundColor: Colors.opacity20,
      borderRadius: 10,
    },
  });
};
